import { useCallback, useEffect, useState } from "react";
import "./project-companies.scss";
import { useTranslation } from "react-i18next";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { setting } from "../../../store/services";
import { showError } from "../../../utils/method";

function AddCompanyModal({ show, onClose, onSubmit, existingCompanyIds = [] }) {
    const { t } = useTranslation();
    const [companies, setCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (show) {
            fetchCompanies();
        } else {
            // Reset state when modal is closed
            setSelectedCompanies([]);
            setError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const result = await setting.getCompanies();

            // Filter out companies that are already assigned
            const availableCompanies = (result.items || []).filter(
                (company) => !existingCompanyIds.includes(company.id),
            );

            setCompanies(availableCompanies);
        } catch (error) {
            showError(t, error);
        } finally {
            setLoading(false);
        }
    };

    // Column definitions for companies grid
    const companyColumns = [
        {
            field: "name",
            headerName: t("companies.name"),
            flex: 1,
            minWidth: 150,
        },
        {
            field: "address",
            headerName: t("companies.address"),
            flex: 2,
            minWidth: 250,
            valueGetter: (params) => {
                const company = params.data;
                if (!company) return "";
                const addressParts = [
                    company.addressStreet,
                    company.addressZipCode,
                    company.addressCity,
                    company.addressCountry,
                ].filter(Boolean);
                return addressParts.length > 0 ? addressParts.join(", ") : "";
            },
        },
        {
            field: "taxId",
            headerName: t("companies.tax-id"),
            flex: 1,
            minWidth: 120,
        },
        {
            field: "phoneNumber",
            headerName: t("companies.phone"),
            flex: 1,
            minWidth: 120,
        },
    ];

    // Handle row selection - updated to handle multiple selections
    const onRowSelection = useCallback((selectedRows) => {
        if (selectedRows?.length > 0) {
            setSelectedCompanies(selectedRows);
            setError("");
        } else {
            setSelectedCompanies([]);
        }
    }, []);

    const handleSubmit = () => {
        if (!selectedCompanies?.length) {
            setError("projects.companies.error-company-required");
            return;
        }

        // Pass all selected company IDs to the parent component
        const companyIds = selectedCompanies.map((company) => company.id);
        onSubmit(companyIds);
    };

    return (
        <ModalAtom
            show={show}
            handleClose={onClose}
            title={t("projects.companies.add-company")}
            saveText={loading ? "common.saving" : "common.save"}
            handleSubmit={handleSubmit}
            customClassName="company-modal"
            body={
                <div className="px-1">
                    {error && <div className="text-danger mb-2">{t(error)}</div>}
                    <div
                        className="modal-grid-350"
                    >
                        <AgGridAtom
                            columnDefs={companyColumns}
                            rowData={companies}
                            onRowSelection={onRowSelection}
                            rowSelection={{
                                mode: 'multiRow',
                                checkboxes: true,
                                headerCheckbox: true
                            }}
                            noRowsMessage={t("companies.no-available-companies")}
                            suppressRowClickSelection={false}
                        />
                    </div>
                </div>
            }
        />
    );
}

export default AddCompanyModal;
